/**
 * docxToMarkdown — browser port of the DocX2MD conversion pipeline.
 *
 * Mirrors the Python project's stages:
 *   preprocess  -> mammoth document transform (monospace-font code detection,
 *                  heading auto-number injection — see DocX2MD preprocess.py)
 *   engine      -> mammoth (.docx -> HTML) + turndown (HTML -> Markdown)
 *   normalize   -> pure text transforms (see DocX2MD normalize.py)
 *
 * Runs fully client-side; no network access during conversion.
 */
import mammoth from 'mammoth';
import JSZip from 'jszip';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';

// From DocX2MD preprocess.py MONOSPACE_FONTS
const MONOSPACE_FONTS = new Set([
    'consolas', 'courier', 'courier new', 'cascadia code', 'cascadia mono',
    'lucida console', 'monaco', 'menlo', 'source code pro', 'fira code',
    'jetbrains mono', 'roboto mono', 'dejavu sans mono',
]);

const HEADING_STYLE_RE = /^Heading([1-6])$/i;

function isMono(font) {
    return !!font && MONOSPACE_FONTS.has(font.trim().toLowerCase());
}

function textRuns(paragraph) {
    return mammoth.transforms
        .getDescendantsOfType(paragraph, 'run')
        .filter((run) => run.children && run.children.some(
            (c) => c.type === 'text' && c.value
        ));
}

/**
 * Document transform. Two jobs, mirroring DocX2MD's docx pre-pass:
 *  1. Paragraphs whose text runs are all monospace-font become code blocks;
 *     monospace runs inside normal paragraphs become inline code.
 *  2. Word's automatic section numbering on headings is injected as literal
 *     text ("## 1.2 Scope"), reconstructed from per-level counters.
 */
/**
 * Word puts automatic heading numbering in the style definitions
 * (styles.xml numPr), which mammoth does not surface. Peek inside the
 * docx to learn which heading levels carry numbering — mirrors the
 * numbering detection in DocX2MD's preprocess.py (GH issue #9).
 */
async function numberedHeadingLevels(input) {
    try {
        const zip = await JSZip.loadAsync(input);
        const styles = await zip.file('word/styles.xml')?.async('string');
        if (!styles) return new Set();
        const levels = new Set();
        for (const block of styles.split(/<w:style\b/).slice(1)) {
            const styleBlock = block.slice(0, block.indexOf('</w:style>'));
            const idMatch = /w:styleId="Heading([1-6])"/.exec(styleBlock);
            if (idMatch && styleBlock.includes('<w:numPr>')) {
                levels.add(parseInt(idMatch[1], 10));
            }
        }
        return levels;
    } catch {
        return new Set();
    }
}

function makeTransform(numberedLevels) {
    const counters = [0, 0, 0, 0, 0, 0];

    function headingNumber(level) {
        counters[level - 1] += 1;
        for (let i = level; i < 6; i++) counters[i] = 0;
        return counters.slice(0, level).join('.');
    }

    function transformParagraph(paragraph) {
        // Monospace-font paragraph -> code block
        const runs = textRuns(paragraph);
        const allMono = runs.length > 0 && runs.every((r) => isMono(r.font));
        if (allMono && !HEADING_STYLE_RE.test(paragraph.styleId || '')) {
            return { ...paragraph, styleId: 'SourceCode', styleName: 'Source Code' };
        }

        // Heading with Word auto-numbering -> inject literal number
        const headingMatch = HEADING_STYLE_RE.exec(paragraph.styleId || '');
        if (
            headingMatch &&
            (paragraph.numbering || numberedLevels.has(parseInt(headingMatch[1], 10)))
        ) {
            const level = parseInt(headingMatch[1], 10);
            const number = headingNumber(level);
            const numberRun = {
                type: 'run',
                children: [{ type: 'text', value: `${number}. ` }],
            };
            // Trailing "." only on top-level numbers, per DocX2MD golden output
            if (level > 1) numberRun.children[0].value = `${number} `;
            return {
                ...paragraph,
                numbering: null,
                children: [numberRun, ...paragraph.children],
            };
        }

        // Monospace runs inside a normal paragraph -> inline code
        if (!allMono && runs.some((r) => isMono(r.font))) {
            return {
                ...paragraph,
                children: paragraph.children.map((child) =>
                    child.type === 'run' && isMono(child.font)
                        ? { ...child, styleId: 'VerbatimChar', styleName: 'Verbatim Char' }
                        : child
                ),
            };
        }

        return paragraph;
    }

    return function transformElement(element) {
        if (element.children) {
            const children = element.children.map(transformElement);
            // Empty paragraphs sandwiched between code paragraphs belong to
            // the code block (preprocess.py rule) — keeps blank lines inside.
            for (let i = 1; i + 1 < children.length; i++) {
                const c = children[i];
                if (
                    c.type === 'paragraph' &&
                    textRuns(c).length === 0 &&
                    children[i - 1].styleId === 'SourceCode' &&
                    children[i + 1].styleId === 'SourceCode'
                ) {
                    children[i] = { ...c, styleId: 'SourceCode', styleName: 'Source Code' };
                }
            }
            element = { ...element, children };
        }
        if (element.type === 'paragraph') {
            element = transformParagraph(element);
        }
        return element;
    };
}

// Word style names -> HTML the turndown stage understands.
const STYLE_MAP = [
    "p[style-name='Title'] => h1:fresh",
    "p[style-name='Source Code'] => pre > code:separator('\\n')",
    "r[style-name='Verbatim Char'] => code",
    "p[style-name='Caption'] => p.caption > em",
    'u => u',
];

function buildTurndown() {
    const td = new TurndownService({
        headingStyle: 'atx',
        codeBlockStyle: 'fenced',
        bulletListMarker: '-',
        emDelimiter: '*',
        strongDelimiter: '**',
        hr: '---',
    });
    td.use(gfm);

    // GFM plugin emits single-tilde ~text~; DocX2MD golden output uses ~~.
    td.addRule('strikethroughDouble', {
        filter: ['del', 's', 'strike'],
        replacement: (content) => `~~${content}~~`,
    });

    // Underline / sub / superscript have no Markdown form; keep the HTML
    // (rendered fine by GitHub/ADO wikis) — mirrors DocX2MD's selective-HTML rule.
    td.keep(['u', 'sub', 'sup']);

    // Tables with merged cells or multi-paragraph cells can't survive as pipe
    // tables; keep them as embedded HTML (DocX2MD SRS behavior). Added last so
    // it takes precedence over the GFM table rule.
    td.addRule('complexTableAsHtml', {
        filter: (node) =>
            node.nodeName === 'TABLE' &&
            (node.querySelector('[colspan], [rowspan]') ||
                Array.from(node.querySelectorAll('td, th')).some(
                    (cell) => cell.querySelectorAll('p').length > 1
                )),
        replacement: (content, node) => `\n\n${node.outerHTML}\n\n`,
    });

    return td;
}

/**
 * Word tables have no header row, so the GFM table rule would keep them as
 * HTML. Promote the first row's cells to <th> in simple tables (no merged
 * cells, no nesting) so they convert to pipe tables — DocX2MD treats the
 * first row as the header (SRS table rules).
 */
function promoteTableHeaders(html) {
    return html.replace(/<table>[\s\S]*?<\/table>/g, (table) => {
        if (
            table.indexOf('<table', 1) !== -1 || // nested table
            /colspan|rowspan/i.test(table) ||    // merged cells -> keep HTML
            /<\/p>\s*<p>/.test(table)            // multi-paragraph cell -> keep HTML
        ) {
            return table;
        }
        // Unwrap the single <p> mammoth puts in each cell so cell content
        // stays on one line in the pipe table.
        table = table
            .replace(/<(td|th)(\b[^>]*)?>\s*<p>/g, '<$1$2>')
            .replace(/<\/p>\s*<\/(td|th)>/g, '</$1>');
        return table.replace(/<tr>[\s\S]*?<\/tr>/, (firstRow) =>
            firstRow.replace(/<td\b/g, '<th').replace(/<\/td>/g, '</th>')
        );
    });
}

/* ---- normalize stage: pure text transforms (port of normalize.py) ---- */

const IMG_TAG_RE = /<img\b([^>]*?)\/?>/gi;
const ATTR_RE = /([a-zA-Z-]+)\s*=\s*"([^"]*)"|([a-zA-Z-]+)\s*=\s*'([^']*)'/g;

function htmlImgToMarkdown(text) {
    return text.replace(IMG_TAG_RE, (_, attrText) => {
        const attrs = {};
        for (const m of attrText.matchAll(ATTR_RE)) {
            attrs[(m[1] || m[3]).toLowerCase()] = m[2] !== undefined ? m[2] : m[4];
        }
        if (!attrs.src) return '';
        return `![${attrs.alt || ''}](${attrs.src})`;
    });
}

function tidyWhitespace(text) {
    return (
        text
            .replace(/\r\n/g, '\n')
            .split('\n')
            .map((line) => line.replace(/[ \t]+$/, ''))
            .join('\n')
            .replace(/\n{3,}/g, '\n\n')
            .replace(/^\n+|\n+$/g, '') + '\n'
    );
}

// Turndown escapes "1." at line starts; injected heading numbers are literal.
function unescapeHeadingNumbers(text) {
    return text.replace(/^(#{1,6} \d+(?:\.\d+)*)\\\./gm, '$1.');
}

function normalize(text) {
    return tidyWhitespace(unescapeHeadingNumbers(htmlImgToMarkdown(text)));
}

/**
 * Convert a .docx ArrayBuffer to Markdown.
 * Images are embedded as base64 data URIs so the output is self-contained.
 *
 * @returns {Promise<{markdown: string, warnings: string[]}>}
 */
export default async function docxToMarkdown(arrayBuffer) {
    // Browser mammoth takes {arrayBuffer}; the Node build (used by tests)
    // takes {buffer}.
    const input =
        typeof window === 'undefined'
            ? { buffer: Buffer.from(arrayBuffer) }
            : { arrayBuffer };

    const numberedLevels = await numberedHeadingLevels(
        typeof window === 'undefined' ? input.buffer : arrayBuffer
    );

    const result = await mammoth.convertToHtml(
        input,
        {
            styleMap: STYLE_MAP,
            transformDocument: makeTransform(numberedLevels),
            // Keep empty paragraphs so blank lines survive inside code blocks;
            // empty <p> elsewhere produces no Markdown output anyway.
            ignoreEmptyParagraphs: false,
        }
    );

    const html = promoteTableHeaders(result.value);
    const markdown = normalize(buildTurndown().turndown(html));
    // Only material fidelity loss should warn (DocX2MD SRS-FR-018);
    // undefined built-in style references degrade quietly.
    const warnings = result.messages
        .filter(
            (m) =>
                m.type === 'warning' &&
                !/style.*hyperlink/i.test(m.message)
        )
        .map((m) => m.message);

    return { markdown, warnings };
}
